<template>
  <zm-alert
    v-if="shouldShowUpsellBanner"
    class="upsell-biz-banner"
    :title="$t('billing.zone.upsell_biz_banner.title')"
    type="success"
    :closable="true"
    :show-icon="false"
    @close="handleClose"
  >
    <span class="upsell-biz-banner__desc">{{ $t('billing.zone.upsell_biz_banner.desc', { price: priceDiff }) }}</span>
    <zm-button @click="handleSwitchToBusiness" type="link">
      {{ $t('billing.zone.upsell_biz_banner.btn') }}
    </zm-button>
  </zm-alert>
</template>

<script>
import { get } from 'lodash';
import { mapGetters } from 'vuex';
import { BILLING_CYCLE, planTypes } from '@/common/constants';
import { ZOOM_MEETING_SUB_TYPES } from '@/plans/ZOOM_MEETING/common/utils';
import { flowTypes } from '@/common/service/page-config';
import { isEmptyVal } from '@/utils';
import Migration from '@/plans/ZOOM_MEETING/dialog/mixins/migration';
import { plansof } from '@/utils/chain';

export default {
  name: 'UpsellBanner',
  mixins: [Migration],
  data() {
    return {
      isDismissed: false,
    };
  },
  computed: {
    ...mapGetters([
      'getSelectedPlans',
      'planMapFlat',
      'partnerRefer',
      'forNewSubscriptionFlow',
      'limitLicenseWithBaseZoneTypes',
      'healthcareAvailable',
      'getCurrentBasePlanInfo',
      'discountWithAmountMap'
    ]),
    shouldShowUpsellBanner() {
      if (this.isDismissed) return false;
      if (this.$context.is.isFreeWinback) return false;
      if (this.$context.flow !== flowTypes.buyNow && this.$context.flow !== flowTypes.basic2pro) return false;
      if (!isEmptyVal(this.partnerRefer) && !this.forNewSubscriptionFlow) return false;
      if (this.discountWithAmountMap?.totalDiscount > 0) return false;
      
      const hasOtherBasePlan = this.getSelectedPlans.some(
        (plan) =>
          plan.type === planTypes.ZONE_PRO_PLUS || // Pro plus
          plan.type === planTypes.ZOOM_ONE_BUNDLE || // Biz plus
          (plan.type === planTypes.ZOOM_MEETING &&
            [ZOOM_MEETING_SUB_TYPES.BIZ, ZOOM_MEETING_SUB_TYPES.BIZ_INR].includes(plan.category)), // Biz
      );
      if (this.proPlanInCart && !hasOtherBasePlan) {
        if (this.discountWithAmountMap?.discountPlans?.[this.proPlanInCart.plan_code]) {
          return false;
        }
        this.$tracking.buyInteraction(
          'banner - product level',
          `zoom workplace pro - need more tools for your business - impression`,
        );
        return true;
      }

      return false;
    },
    proPlanInCart() {
      return this.getSelectedPlans.find(
        (plan) =>
          plan.type === planTypes.ZOOM_MEETING &&
          plan.category === ZOOM_MEETING_SUB_TYPES.PRO &&
          (plan.billing_cycle === BILLING_CYCLE.MONTHLY ||
            plan.billing_cycle === BILLING_CYCLE.ANNUAL ||
            plan.billing_cycle === BILLING_CYCLE.ANNUAL_TERM_MONTHLY),
      );
    },
    bizPlanCandidate() {
      let billingCycle = this.proPlanInCart.billing_cycle;
      if (billingCycle === BILLING_CYCLE.ANNUAL_TERM_MONTHLY) {
        billingCycle = BILLING_CYCLE.ANNUAL;
      }
      return plansof(this.planMapFlat)
        .type(planTypes.ZOOM_MEETING)
        .sub_type(ZOOM_MEETING_SUB_TYPES.BIZ)
        .cycle(billingCycle)
        .get(0);
    },
    priceDiff() {
      let priceDiff = 0;
      if (
        this.proPlanInCart.billing_cycle === BILLING_CYCLE.ANNUAL ||
        this.proPlanInCart.billing_cycle === BILLING_CYCLE.ANNUAL_TERM_MONTHLY
      ) {
        priceDiff = (this.bizPlanCandidate.amount - this.proPlanInCart.amount) / 12;
      } else {
        priceDiff = this.bizPlanCandidate.amount - this.proPlanInCart.amount;
      }
      return this.$money(priceDiff, { forceDiff: true });
    },
    currentBaseCategory() {
      const { planType, plans } = this.getCurrentBasePlanInfo;
      if(planType !== planTypes.ZOOM_MEETING || !plans[0]) {
        return '';
      }
      return get(plans, '0.zoomMeetingCategory', '');
    }
  },
  methods: {
    handleClose() {
      this.$tracking.buyInteraction(
        'banner - product level',
        `zoom workplace pro - need more tools for your business - close`,
      );
      this.isDismissed = true;
    },
    async handleSwitchToBusiness() {
      if (!this.proPlanInCart) return;
      this.$tracking.buyInteraction(
        'banner - product level',
        `zoom workplace pro - need more tools for your business - switch to business`,
      );
      if (this.healthcareAvailable) {
        try {
          await this.handleConfirmHealthcareSwitchPlan();
        } catch (err) {
          return;
        }
      }
      await this.switchMeetingPlanCategory({ category: ZOOM_MEETING_SUB_TYPES.BIZ, btn: { disabled: false } });
    },
    handleSure() {
      
    }
  },
};
</script>
<style lang="scss" scoped>
.upsell-biz-banner {
  margin-bottom: 16px;
  &__desc {
    margin-right: 8px;
  }

  ::v-deep {
    .zm-alert {
      &__body {
        padding: 0;
        line-height: 18px;
      }
    }
    .zm-button {
      padding: 0;
    }
  }
}
</style>
